import { useState, useMemo, lazy, Suspense } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Copy,
  QrCode,
  ExternalLink,
  Trash2,
  Download,
  ScanLine,
  Calendar,
  Link2,
  Activity,
  ArrowRight,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';

// ⚡ Performance Optimization: Lazy load QR code library
const LazyQRCodeCanvas = lazy(() =>
  import('qrcode.react').then((module) => ({
    default: module.QRCodeCanvas,
  }))
);
import { copyToClipboard, formatNumber, formatDate } from '@/client/lib/utils';
import { useUIStore } from '@/client/stores/ui-store';
import { apiFetch } from '@/client/lib/api';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent } from '@/client/components/ui/card';
import { Skeleton } from '@/client/components/ui/skeleton';

interface QRCodeData {
  id: string;
  name: string;
  description?: string | null;
  targetUrl: string;
  linkId?: string | null;
  projectId: string;
  workspaceId?: string;
  styleOptions?: {
    darkColor?: string;
    lightColor?: string;
  };
  logoOptions?: {
    type: 'default' | 'custom' | 'none';
    customUrl?: string;
  };
  totalScans: number;
  createdAt: string;
  updatedAt: string;
  // If linked to a short link, we get its analytics
  link?: {
    shortCode: string;
    shortUrl: string;
    totalClicks: number;
  };
}

export function QRCodeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { openDeleteConfirmation } = useUIStore();

  // Copy state
  const [copied, setCopied] = useState(false);

  // Fetch QR code details
  const { data: qrCode, isLoading } = useQuery<QRCodeData>({
    queryKey: ['qr-codes', id],
    queryFn: async () => {
      const response = await apiFetch(`/api/qr-codes/${id}`);
      if (!response.ok) throw new Error('QR code not found');
      return response.json();
    },
  });

  // Check if this QR code points to an arml.ink link for analytics
  const linkedShortCode = useMemo(() => {
    if (!qrCode?.targetUrl) return null;
    const match = qrCode.targetUrl.match(/arml\.ink\/([a-zA-Z0-9]+)/);
    return match ? match[1] : null;
  }, [qrCode?.targetUrl]);

  const handleCopyUrl = async () => {
    if (!qrCode) return;
    await copyToClipboard(qrCode.targetUrl);
    setCopied(true);
    toast.success('URL copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!qrCode) return;
    const canvas = document.getElementById('qr-code-detail-canvas') as HTMLCanvasElement;
    if (!canvas) return;

    const link = document.createElement('a');
    link.download = `qr-${qrCode.name.toLowerCase().replace(/\s+/g, '-')}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('QR code downloaded!');
  };

  const handleDelete = () => {
    if (qrCode) {
      openDeleteConfirmation('qr-code', qrCode.id, qrCode.name);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="size-10 rounded-lg" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="h-64 rounded-2xl" />
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="h-32 rounded-2xl" />
            <Skeleton className="h-64 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!qrCode) {
    return (
      <div className="text-center py-12">
        <QrCode className="size-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">QR Code not found</h2>
        <p className="text-muted-foreground mb-4">
          This QR code may have been deleted or you don&apos;t have permission to view it.
        </p>
        <Button asChild>
          <Link to="/qr-codes">Back to QR Codes</Link>
        </Button>
      </div>
    );
  }

  const fgColor = qrCode.styleOptions?.darkColor || '#000000';
  const bgColor = qrCode.styleOptions?.lightColor || '#ffffff';
  const showLogo = qrCode.logoOptions?.type !== 'none';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild className="rounded-xl">
            <Link to="/qr-codes">
              <ArrowLeft className="size-5" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
              {qrCode.name}
            </h1>
            {qrCode.description && (
              <p className="text-muted-foreground mt-1">{qrCode.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleDownload} className="rounded-xl">
            <Download className="size-4" />
            Download
          </Button>
          <Button
            variant="outline"
            onClick={handleDelete}
            className="rounded-xl hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50"
          >
            <Trash2 className="size-4" />
            Delete
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left: QR Code Preview */}
        <Card className="rounded-2xl py-0">
          <CardContent className="p-6">
            <div className="flex flex-col items-center">
              <div className="p-6 rounded-xl border bg-white mb-4">
                <Suspense
                  fallback={
                    <div
                      className="bg-muted animate-pulse rounded"
                      style={{ width: 200, height: 200 }}
                    />
                  }
                >
                  <LazyQRCodeCanvas
                    id="qr-code-detail-canvas"
                    value={qrCode.targetUrl}
                    size={200}
                    level={showLogo ? 'H' : 'M'}
                    marginSize={1}
                    fgColor={fgColor}
                    bgColor={bgColor}
                    imageSettings={
                      showLogo && qrCode.logoOptions?.type === 'default'
                        ? {
                            src: '/assets/logo.png',
                            height: 40,
                            width: 40,
                            excavate: true,
                          }
                        : showLogo && qrCode.logoOptions?.customUrl
                          ? {
                              src: qrCode.logoOptions.customUrl,
                              height: 40,
                              width: 40,
                              excavate: true,
                            }
                          : undefined
                    }
                  />
                </Suspense>
              </div>

              {/* Stats */}
              <div className="w-full space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <ScanLine className="size-4" />
                    Total Scans
                  </span>
                  <span className="font-bold tabular-nums">{formatNumber(qrCode.totalScans)}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <Calendar className="size-4" />
                    Created
                  </span>
                  <span className="text-sm">{formatDate(qrCode.createdAt)}</span>
                </div>
                {linkedShortCode && (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <span className="text-sm text-muted-foreground flex items-center gap-2">
                      <Link2 className="size-4" />
                      Linked
                    </span>
                    <Link
                      to={`/links/${linkedShortCode}`}
                      className="text-sm text-primary hover:underline font-mono"
                    >
                      arml.ink/{linkedShortCode}
                    </Link>
                  </div>
                )}
              </div>

              {/* Target URL */}
              <div className="w-full mt-4 pt-4 border-t">
                <p className="text-xs text-muted-foreground mb-2">Target URL</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-muted rounded-lg text-xs font-mono truncate">
                    {qrCode.targetUrl}
                  </code>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={handleCopyUrl}
                    className="flex-shrink-0"
                  >
                    {copied ? (
                      <Check className="size-4 text-emerald-600" />
                    ) : (
                      <Copy className="size-4" />
                    )}
                  </Button>
                  <Button variant="ghost" size="icon-sm" asChild className="flex-shrink-0">
                    <a href={qrCode.targetUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="size-4" />
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Right: Analytics */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="rounded-2xl border-dashed bg-muted/30 py-0">
            <CardContent className="p-8 text-center">
              <div className="size-16 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center mx-auto mb-4">
                <Activity className="size-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Detailed Analytics</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                View scan performance data, including timeline, geographic distribution, and device
                breakdown in the Analytics Hub.
              </p>
              <Button asChild className="rounded-xl" size="lg">
                <Link to={`/analytics?resourceTypes=qr&resourceIds=${id}`}>
                  View Full Analytics
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
